import type { NextPage } from 'next';

import { format } from 'date-fns';

import { Footer, GradientBg, Header } from '../components';

const LAST_UPDATED = new Date('5/27/26');

const Privacy: NextPage = () => {
  return (
    <>
      <GradientBg />
      <Header />
      <div className="relative mx-auto py-16 lg:py-32 px-4 prose prose-lg prose-a:text-primary hover:prose-a:text-primaryHover">
        <div className="text-center">
          <h1>Coverdash Privacy Policy</h1>
          <p className="font-semibold">
            Last updated: {format(LAST_UPDATED, 'MM/dd/yy')}
          </p>
        </div>
        <div className="mt-16">
          <p>
            Coverdash, Inc. (“Coverdash”) offers freelancers, e-commerce
            merchants, and all small business owners tailored insurance
            solutions and instant coverage
          </p>

          <p>
            This Privacy Policy describes how Coverdash collects, uses,
            discloses and otherwise processes personal information in connection
            with our websites, applications, and online services that link to
            this Privacy Policy (collectively, “the Services”), and explains the
            rights and choices available to individuals with respect to their
            information.
          </p>

          <p>
            Our Services are designed for use by businesses and are not intended
            for personal or household use. Accordingly, we treat all personal
            information covered by this Privacy Policy, including information
            about any visitors to our website, as pertaining to individuals
            acting as business representatives, rather than in their personal
            capacity.
          </p>

          <h2>Personal Information We Collect</h2>

          <h3>Information you provide to us.</h3>
          <p>
            If you are a visitor to a Coverdash website or insurance experience
            via an affinity partner for any reason (including for purposes of
            obtaining a free trial) or a Coverdash customer who registers or
            signs into a website maintained by Coverdash, we may obtain various
            types of information from you concerning your access device through
            the Services. This information may include, but is not limited to,
            the following information that can itself be used to identify you
            and allow the holder to contact you:
          </p>
          <ul>
            <li>
              <strong>Contact details</strong>, such as your first and last
              name, phone number, and email address.
            </li>
            <li>
              <strong>Business personal information</strong>, such as your FEIN
              number, employment details, revenue information and payroll
              information.
            </li>
            <li>
              <strong>Payment and transactional data</strong> needed to process
              your application or collect any fees or premiums associated with
              your application through the Services (including name, payment
              card information, bank account number, billing information), and
              your purchase history. This information is processed directly by
              our third-party payment processors. We do not store any payment
              card information.
            </li>
            <li>
              <strong>Communications</strong> that we exchange with you,
              including when you contact us with questions, feedback, or
              otherwise.
            </li>
            <li>
              <strong>Marketing data</strong>, such as your preferences for
              receiving our marketing communications, and details about your
              engagement with those communications.
            </li>
          </ul>

          <h3>Third party sources.</h3>
          <p>
            We may combine personal information we receive from you with
            personal information we obtain from other sources, such as:
          </p>

          <ul>
            <li>
              <strong>Data providers</strong>, such as information services and
              data licensors.
            </li>
            <li>
              <strong>Affinity partners</strong>, who can provide your name,
              phone, number, email, and other relevant business information.
            </li>
            <li>
              <strong>Public sources</strong>, such as social media platforms.
            </li>
          </ul>

          <h3>Automatic Data Collection</h3>
          <p>
            We may automatically log information about your interactions with
            our Services and communications, such as:
          </p>

          <ul>
            <li>
              <strong>Device data</strong>, such as computer or mobile device
              operating system type and version, manufacturer and model, browser
              type, screen resolution, RAM and disk size, CPU usage, device type
              (e.g., phone, tablet), IP address, unique identifiers (including
              identifiers used for advertising purposes), language settings,
              mobile device carrier, radio/network information (e.g., WiFi, LTE,
              3G), and general location information such as city, state, or
              geographic area.
            </li>
            <li>
              Online activity data, such as pages or screens viewed, how long
              you spend on a page or screen, the website you visited before
              browsing to our website, navigation paths between pages or
              screens, information about activity on a page or screen, access
              times and duration of access, and whether you have opened our
              marketing emails or clicked links within them. We use the
              following tools for automatic data collection:
            </li>
            <li>
              <strong>Cookies</strong>, which are text files that websites store
              on a visitor's device to uniquely identify the visitor's browser
              or to store information or settings in the browser for the purpose
              of helping you navigate between pages efficiently, remembering
              your preferences, enabling functionality, helping us understand
              user activity and patterns, and facilitating online advertising.
            </li>
            <li>
              <strong>Local storage technologies</strong>, like HTML5, that
              provide cookie-equivalent functionality but can store larger
              amounts of data, including on the device outside of your browser
              in connection with specific applications.
            </li>
            <li>
              <strong>Web beacons</strong>, also known as pixel tags or clear
              GIFs, which are used to demonstrate that a webpage or email was
              accessed or opened, or that certain content was viewed or clicked.
            </li>
          </ul>

          <h3>How We Use Personal Information</h3>
          <p>We use your personal information for the following purposes:</p>
          <ul>
            <li>
              <strong>Service delivery</strong>, including to:
              <ul>
                <li>
                  Fulfill your requests for selecting and purchasing insurance
                  offers;
                </li>
                <li>
                  Provide, operate, and improve our Services and our business;
                </li>
                <li>
                  Process your payments and complete transactions with you;
                </li>
                <li>
                  Understand your needs and interests, and personalize your
                  experience with the Service and our communications;
                </li>
                <li>
                  Communicate with you about our Services, including by sending
                  announcements, updates, security alerts, and support and
                  administrative messages;
                </li>
                <li>
                  Provide support, and respond to requests, questions, and
                  feedback.
                </li>
                <li>
                  Market our affinity partners services that cater to your
                  specific business needs.
                </li>
              </ul>
            </li>
            <li>
              <strong>Research and development</strong>, including to:
              <ul>
                <li>Develop new features, products, and services; and</li>
                <li>
                  Create aggregated, de-identified, or other anonymous data or
                  analytics
                </li>
              </ul>
              <li>
                <strong>Direct marketing and advertising.</strong> We and our
                advertising partners may use personal information for marketing
                and advertising purposes, including:
                <ul>
                  <li>
                    <strong>Direct Marketing.</strong> We may send you direct
                    marketing communications as permitted by law, including
                    notifying you of special promotions and offers. You may opt
                    out of our marketing communications as described in the “Opt
                    out of marketing communications” section below.
                  </li>
                  <li>
                    <strong>Advertising.</strong> We engage our advertising
                    partners, including third party advertising companies and
                    social media companies, to market our own Services and grow
                    our Services' user base, such as to provide targeted
                    marketing about our own Services via third-party services.
                    These companies may use cookies and similar technologies to
                    collect information about your interaction (including the
                    data described in the “Automatic Data Collection” section
                    above) over time across the Services, our communications and
                    other online services, and use that information to serve
                    online ads about our Services. If you prefer not to share
                    your personal information with our advertising partners, you
                    may follow the instructions in the “Online tracking opt-out”
                    section below.
                  </li>
                  <li>
                    <strong>Affinity Marketing:</strong> We may send you direct
                    marketing on behalf of our affinity partners to advertise
                    their services and products. The services and products that
                    are marketed to you will be tailored to your assumed
                    business needs based on our most current affinity
                    relationships.
                  </li>
                </ul>
              </li>
              <li>
                <strong>Compliance and protection</strong>, including to:
                <ul>
                  <li>
                    Comply with applicable laws, lawful requests, and legal
                    process, such as to respond to subpoenas or requests from
                    government authorities;
                  </li>
                  <li>
                    Protect our, your, or others' rights, privacy, safety, or
                    property (including by making and defending legal claims);
                  </li>
                  <li>
                    Audit our internal processes for compliance with legal and
                    contractual requirements and internal policies;
                  </li>
                  <li>
                    Enforce the terms and conditions that govern our Services;
                    and
                  </li>
                  <li>
                    Prevent, identify, investigate, and deter fraudulent,
                    harmful, unauthorized, unethical, or illegal activity,
                    including cyberattacks and identity theft.
                  </li>
                </ul>
              </li>
            </li>
          </ul>

          <h3>How We Share Personal Information</h3>
          <p>We may share your personal information with:</p>
          <ul>
            <li>
              <strong>Brokers.</strong> Appointed brokers may have access to
              your personal information in order to assist in the submission and
              completion of your applications, or to jointly offer content,
              promotions, and other service offerings with us.
            </li>
            <li>
              <strong>Service providers.</strong> Companies and individuals that
              provide services on our behalf or help us operate our Services or
              our business (such as hosting, information technology, customer
              support, database management systems, email delivery, payment
              processors, and website analytics services).
            </li>
            <li>
              <strong>AI assistants and connectors.</strong> If you choose to
              connect Coverdash to a third-party AI assistant or platform (for
              example, by enabling the Coverdash connector in Anthropic's Claude),
              we share the Coverdash data you request through that integration —
              such as your policies, coverage details, documents, certificates of
              insurance, and support tickets, and, for partner accounts, portfolio
              analytics — with that provider solely to fulfill the requests you
              make. This sharing occurs only after you authorize the connection,
              is limited to your own account data, and is governed by that
              provider's terms and privacy policy. We do not authorize these
              providers to use your information to train their AI models.
            </li>
            <li>
              <strong>Advertising partners.</strong> We may share your personal
              information with third party advertising companies, including for
              the interest-based advertising purposes described above.
            </li>
            <li>
              <strong>Affinity Partners.</strong> We may share your personal
              information with our affinity partners in order for them to
              advertise their services to you.
            </li>
            <li>
              <strong>Professional advisors.</strong> Professional advisors,
              such as lawyers, auditors, bankers, and insurers, where necessary
              in the course of the professional services that they render to us.
            </li>
            <li>
              <strong>Authorities and others.</strong> Law enforcement,
              government authorities, and private parties, as we believe in good
              faith to be necessary or appropriate for the compliance and
              protection purposes described above.
            </li>
            <li>
              <strong>Business transferees.</strong> Acquirers and other
              relevant participants in business transactions (or negotiations
              for such transactions) involving a corporate divestiture, merger,
              consolidation, acquisition, reorganization, sale, or other
              disposition of all or any portion of the business or assets of, or
              equity interests in, USL or our affiliates (including in
              connection with a bankruptcy or similar proceedings).
            </li>
          </ul>

          <h3>Privacy Choices</h3>
          <ul>
            <li>
              <strong>Access to information.</strong> To keep your information
              accurate, current, and complete, please contact us as specified
              below. We will take reasonable steps to update or correct
              information in our possession that you have previously submitted
              via the Services.
            </li>
            <li>
              <strong>Opt out of marketing communications.</strong> You may opt
              out of marketing-related emails by following the opt-out or
              unsubscribe instructions at the bottom of the emails you receive
              from us. You may continue to receive service-related and other
              non-marketing emails.
            </li>
            <li>
              <strong>Online tracking opt-out.</strong> There are a number of
              ways to limit online tracking, which we have summarized below:
              <ul>
                <li>
                  <strong>Blocking cookies in your browser.</strong> Most
                  browsers let you remove or reject cookies, including cookies
                  used for interest-based advertising. To do this, follow the
                  instructions in your browser settings. Many browsers accept
                  cookies by default until you change your settings. For more
                  information about cookies, including how to see what cookies
                  have been set on your device and how to manage and delete
                  them, visit www.allaboutcookies.org.
                  <br />
                  Use the following links to learn more about how to control
                  cookies and online tracking through your browser:
                  <ul>
                    <li>
                      <a href="https://support.mozilla.org/en-US/kb/enhanced-tracking-protection-firefox-desktop">
                        Firefox
                      </a>
                    </li>
                    <li>
                      <a href="https://support.google.com/chrome/answer/95647">
                        Chrome
                      </a>
                    </li>
                    <li>
                      <a href="https://support.microsoft.com/en-us/microsoft-edge/learn-about-tracking-prevention-in-microsoft-edge-5ac125e8-9b90-8d59-fa2c-7f2e9a44d869">
                        Microsoft Edge
                      </a>
                    </li>
                    <li>
                      <a href="https://support.apple.com/guide/safari/manage-cookies-and-website-data-sfri11471/mac">
                        Safari
                      </a>
                    </li>
                  </ul>
                </li>
                <li>
                  <strong>
                    Blocking advertising ID use in your mobile settings.
                  </strong>{' '}
                  Your mobile device settings may provide functionality to limit
                  use of the advertising ID associated with your mobile device
                  for interest-based advertising purposes.
                </li>
                <li>
                  <strong>Using privacy plug-ins or browsers.</strong> You can
                  block our websites from setting cookies used for
                  interest-based ads by using a browser with privacy features,
                  like Brave, or installing browser plugins like Privacy Badger,
                  Ghostery, or uBlock Origin, and configuring them to block
                  third party cookies/trackers. You can also opt out of Google
                  Analytics by downloading and installing the browser plug-in
                  available at: https://tools.google.com/dlpage/gaoptout.
                </li>
                <li>
                  <strong>Platform opt-outs.</strong> Some third-party ad
                  networks, including third-party ad servers, ad agencies, ad
                  technology vendors and research firms, allow you to opt-out
                  directly by using their opt-out tools. Some of these
                  providers, and links to their opt-out tools, are:
                  <ul>
                    <li>
                      <a href="https://adssettings.google.com/">Google</a>
                    </li>
                    <li>
                      <a href="https://www.facebook.com/about/ads">Facebook</a>
                    </li>
                    <li>
                      <a href="https://twitter.com/settings/account/personalization?lang=en">
                        Twitter
                      </a>
                    </li>
                  </ul>
                </li>
                <li>
                  <strong>Advertising industry opt-out tools.</strong> You can
                  also use these opt-out options to limit use of your
                  information for interest-based advertising by participating
                  companies:
                  <ul>
                    <li>
                      <a href="http://optout.aboutads.info/">
                        Digital Advertising Alliance
                      </a>
                    </li>
                    <li>
                      <a href="http://optout.networkadvertising.org/?c=1">
                        Network Advertising Initiative
                      </a>
                    </li>
                  </ul>
                </li>
              </ul>
            </li>
          </ul>

          <h3>Other Sites and Services</h3>
          <p>
            The Services may contain links to websites and other online services
            operated by third parties. In addition, our content may be
            integrated into web pages or other online services that are not
            associated with us. These links and integrations are not an
            endorsement of, or representation that we are affiliated with, any
            third party. We do not control websites or online services operated
            by third parties, and we are not responsible for their actions.
          </p>

          <h3>Data Retention</h3>
          <p>
            We retain personal information for as long as necessary to provide
            the Services, maintain and service your insurance coverage, comply
            with our legal, regulatory, tax, accounting, and recordkeeping
            obligations (including insurance and financial-services
            requirements), resolve disputes, and enforce our agreements.
            Retention periods vary depending on the type of information and the
            purpose for which it was collected. When personal information is no
            longer needed for these purposes, we take reasonable steps to
            delete, anonymize, or otherwise stop using it.
          </p>

          <h3>Information Security</h3>
          <p>
            We employ a number of technical, organizational, and physical
            safeguards designed to protect the personal information we collect.
            However, no security measures are failsafe and we cannot guarantee
            the security of personal information.
          </p>

          <h3>Children</h3>
          <p>
            Our Services are not intended for use by children under 13 years of
            age. If we learn that we have collected personal information through
            our Services from a child under 13 without the consent of the
            child's parent or guardian as required by law, we will delete it.
          </p>

          <h3>Changes to This Privacy Policy</h3>
          <p>
            We reserve the right to modify this Privacy Policy at any time. If
            we make material changes to this Privacy Policy, we will provide a
            notification by updating the date of this Privacy Policy and posting
            it on the website.
          </p>

          <h3>How to Contact Us</h3>
          <p>
            You can reach us by email at legal@coverdash.com or at the following
            mailing address:
          </p>
          <div className="">
            <span className="font-semibold block">Coverdash, Inc.</span>
            <span className="">1235 Broadway, New York, NY 10001</span>
          </div>
        </div>
        <p className="text-center">Coverdash: Business insurance in a Dash</p>
      </div>
      <Footer />
    </>
  );
};

export default Privacy;
